import {
  Button,
  VStack,
  HStack,
  Flex,
  Skeleton,
  InputGroup,
  Input,
  Td,
  Th,
  Thead,
  Tr,
  Tbody,
  Table,
  useToast,
} from '@chakra-ui/react';
import React from 'react';
import {
  Link as RouterLink,
  Navigate,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { z } from 'zod';

import { finleyAssert } from '@finley-technologies/common';

import { ResolvedLoanFragmentGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { PricingOptionDetails } from './components/PricingOptionDetails';
import { colors } from 'packages/common-frontend/colors';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { Breadcrumbs } from '~/packages/common-frontend/components/Breadcrumbs';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { successToastConfig } from '~/packages/common-frontend/constants';
import {
  numberToPercentageLabel,
  truncateUuid,
} from '~/packages/common-frontend/utils';
import { useFundingRequestTemplateContextV1 } from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';
import { ClassedDrawRequests } from '~/packages/funding-requests/ui/submit/CreateRequest/ClassedDrawRequestAmount';
import { CurrencyInput } from '~/packages/funding-requests/ui/submit/CreateRequest/CurrencyInput';
import { sharedSectionStyles } from '~/packages/funding-requests/ui/submit/CreateRequest/Shared';
import {
  useGetResolvedLoanByIdQuery,
  useApproveAndExecuteLoanEditMutation,
  useCancelLoanEditMutation,
} from '~/packages/loans/requests/loans.gql.generated';

// Types
const RouteParamsStruct = z.object({
  loanId: z.string({ message: 'Missing URL parameter' }),
});

// TODO: @markhaghani, merge this with the LoanDetails component in EditLoanView
const LoanDetails = ({ loan }: { loan: ResolvedLoanFragmentGQLType }) => {
  return (
    <VStack
      sx={{
        w: '100%',
        alignItems: 'flex-start',
      }}
    >
      <Typography variant="section-title">Loan Details</Typography>
      <VStack
        sx={{
          alignItems: 'flex-start',
          borderRadius: 'md',
          borderWidth: '1px',
          gap: '1rem',
          p: '2rem',
          w: '100%',
        }}
      >
        <HStack {...sharedSectionStyles}>
          <Flex w="25%" alignSelf="flex-start">
            <Typography fontWeight="bold">External Loan ID</Typography>
          </Flex>
          <VStack alignItems="flex-start" w="100%">
            <InputGroup w="100%" bgColor="white">
              <Input
                bgColor="white"
                isDisabled
                type="text"
                value={loan.externalLoanId ?? ''}
                w="100%"
              />
            </InputGroup>
          </VStack>
        </HStack>
        <HStack {...sharedSectionStyles}>
          <Flex w="25%" alignSelf="flex-start">
            <Typography fontWeight="bold">Pricing Option</Typography>
          </Flex>
          <PricingOptionDetails loan={loan} isDisabled />
        </HStack>
        <HStack {...sharedSectionStyles}>
          <Flex w="25%" alignItems="center">
            <Typography fontWeight="bold">Maturity Date</Typography>
          </Flex>
          <VStack alignItems="flex-start" w="100%">
            <InputGroup w="100%" bgColor="white">
              <Input
                isDisabled
                type="date"
                w="100%"
                value={loan.dates.maturityDate}
              />
            </InputGroup>
          </VStack>
        </HStack>
      </VStack>
    </VStack>
  );
};

// TODO: @markhaghani, merge this with the FundingRequests component in EditLoanView
const FundingRequests = ({ loan }: { loan: ResolvedLoanFragmentGQLType }) => {
  const {
    isLoading: isFundingRequestTemplateLoading,
    context: {
      data: { expectedSubmission },
    },
  } = useFundingRequestTemplateContextV1();

  if (isFundingRequestTemplateLoading) return <Skeleton w="100%" />;

  return (
    <VStack
      sx={{
        alignItems: 'flex-start',
        mt: '1rem',
        gap: '2rem',
        w: '100%',
      }}
    >
      <Typography variant="section-title" trim>
        Funding Requests
      </Typography>
      {loan.funding.requests.map((fundingRequest) => (
        <VStack
          key={fundingRequest.fundingRequestId}
          alignItems="flex-start"
          sx={{
            borderRadius: 'md',
            borderWidth: '1px',
            gap: '1rem',
            p: '2rem',
            w: '100%',
          }}
        >
          <Typography variant="section-subtitle">
            Funding Request {truncateUuid(fundingRequest.fundingRequestId)}
          </Typography>
          <VStack
            sx={{
              padding: '16px',
              backgroundColor: colors.millet[50],
              width: '100%',
              borderRadius: '8px',
              gap: '2rem',
            }}
          >
            <HStack w="100%" alignItems="flex-start">
              <Flex w="25%">
                <Typography fontWeight="bold">Draw Request Amount</Typography>
              </Flex>
              <VStack w="100%" alignItems="flex-start">
                <ClassedDrawRequests />
                <HStack alignSelf="flex-end" spacing="24px" />

                <VStack
                  sx={{
                    spacing: '24px',
                    py: '16px',
                    w: '100%',
                  }}
                  border="1px solid"
                  borderColor={colors.millet[50]}
                  borderRadius="8px"
                >
                  <Table>
                    <Thead>
                      <Tr>
                        <Th>Lender</Th>
                        <Th>Lender Share</Th>
                        <Th>Outstanding Balance</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {/* // TODO: @markhaghani, need to set up initial lender shares state */}
                      {expectedSubmission.draw.requests.map((r) =>
                        r.shares.map((lenderShare) => (
                          <Tr key={lenderShare.lender.organizationId}>
                            <Td>
                              <Flex alignSelf="flex-start" alignItems="start">
                                {lenderShare.lender.name}
                              </Flex>
                            </Td>
                            <Td>
                              {typeof lenderShare.percent === 'number'
                                ? numberToPercentageLabel(lenderShare.percent)
                                : '-'}
                            </Td>
                            <Td>
                              <VStack alignSelf="flex-start" gap="20px">
                                <CurrencyInput
                                  state={lenderShare.amount}
                                  updateState={() => {}}
                                  isDisabled
                                />
                              </VStack>
                            </Td>
                          </Tr>
                        )),
                      )}
                    </Tbody>
                  </Table>
                </VStack>
              </VStack>
            </HStack>
            <HStack {...sharedSectionStyles} spacing="0">
              <Flex w="25%" alignItems="center">
                <Typography fontWeight="bold">Funding Date </Typography>
              </Flex>
              <InputGroup bgColor="white">
                <Input
                  isDisabled
                  type="date"
                  value={expectedSubmission.draw.global.targetFundingDate}
                />
              </InputGroup>
            </HStack>
          </VStack>
        </VStack>
      ))}
    </VStack>
  );
};

export const ReviewLoanEditView = () => {
  const {
    context: fundingTemplateContext,
    isLoading: isFundingRequestTemplateLoading,
  } = useFundingRequestTemplateContextV1();

  const { user } = useUserAuthOrThrow();
  const toast = useToast();
  const navigate = useNavigate();

  const params = useParams();
  const { loanId } = RouteParamsStruct.parse(params);

  const { error: loanError, data: loanData } = useGetResolvedLoanByIdQuery({
    fetchPolicy: 'network-only',
    variables: { loanId },
  });

  const {
    error: pendingLoanError,
    data: pendingLoanData,
    loading: isPendingLoanLoading,
  } = useGetResolvedLoanByIdQuery({
    fetchPolicy: 'network-only',
    skip: !loanData?.loan.edits?.destination?.loanId,
    variables: { loanId: loanData?.loan.edits?.destination?.loanId ?? '' },
  });

  if (loanError) throw loanError;
  if (pendingLoanError) throw pendingLoanError;

  const [
    approveAndExecuteEditApi,
    approveAndExecuteMutation,
  ] = useApproveAndExecuteLoanEditMutation();
  if (approveAndExecuteMutation.error) throw approveAndExecuteMutation.error;

  const [
    cancelLoanEditApi,
    cancelLoanEditMutation,
  ] = useCancelLoanEditMutation();
  if (cancelLoanEditMutation.error) throw cancelLoanEditMutation.error;

  if (isFundingRequestTemplateLoading || isPendingLoanLoading) {
    return <Skeleton h="600px" />;
  }

  if (
    !loanData ||
    !loanData.loan.edits.destination ||
    loanData.loan.edits.destination.status !== 'pending' ||
    !pendingLoanData
  ) {
    toast({
      title:
        'Loan or pending loan edit not found. Please contact Finley for support.',
      status: 'error',
      position: 'top',
    });
    return <Navigate to={`/loans/${loanId}`} />;
  }
  const pendingDestination = loanData.loan.edits.destination;

  // TODO: @markhaghani, remove this once Context is updated to deal with loading / ready states - https://github.com/finleytechnologies/atlas/pull/14181#discussion_r1890687834
  // Getting an empty initial state for fundingTemplateContext
  if (fundingTemplateContext.creditAgreementId) {
    finleyAssert(
      fundingTemplateContext.allowedActions === 'readOnly',
      `Expect funding request context to be readOnly on Review Loan page`,
    );
  }

  return (
    <VStack spacing="2rem" alignItems="flex-start">
      <VStack spacing="2rem" w="100%" alignItems="flex-start">
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Funding', path: '/' },
            { label: 'Loans', path: '/loans' },
            { label: `${loanId}`, path: `/loans/${loanId}` },
          ]}
        />
        <HStack w="100%" justifyContent="space-between" alignItems="center">
          <Typography variant="page-title" trim>
            Review Loan
          </Typography>
          <Flex gap="1rem">
            <Button
              as={RouterLink}
              to={`/loans/${loanId}`}
              variant="secondary-muted"
            >
              View Current Loan
            </Button>
          </Flex>
        </HStack>
      </VStack>

      <LoanDetails loan={pendingLoanData.loan} />
      <FundingRequests loan={pendingLoanData.loan} />

      <HStack w="100%" justifyContent="flex-end" spacing="1rem">
        <Button
          as={RouterLink}
          to={`/loans/${loanId}`}
          colorScheme="v2-aurora"
          variant="secondary"
        >
          Back
        </Button>
        <Button
          as={RouterLink}
          colorScheme="v2-aurora"
          isLoading={cancelLoanEditMutation.loading}
          onClick={async () => {
            await cancelLoanEditApi({
              variables: {
                loanActionId: pendingDestination.loanActionId,
              },
            });

            toast({
              ...successToastConfig,
              title: 'Successfully canceled loan edit',
            });

            navigate(`/loans/${loanData.loan.id}`);
          }}
          variant="secondary"
        >
          Cancel
        </Button>
        <Button
          as={RouterLink}
          colorScheme="v2-aurora"
          isLoading={approveAndExecuteMutation.loading}
          onClick={async () => {
            await approveAndExecuteEditApi({
              variables: {
                originalLoanId: loanData.loan.id,
                newLoanId: pendingDestination.loanId,
                loanActionId: pendingDestination.loanActionId,
                approvedUserId: user.id, // TODO: @markhaghani, set up guardrails?
              },
            });

            toast({
              ...successToastConfig,
              title: 'Successfully approved loan edit',
            });

            navigate(`/loans/${pendingDestination.loanId}`);
          }}
          variant="secondary"
        >
          Approve
        </Button>
      </HStack>
    </VStack>
  );
};
